import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import fetchWithCredentials from "apiClient";
import { CondoCase } from "types/CondoCase";
import { useAuth } from "context/AuthContext";

interface CasesContextType {
  cases: CondoCase[];
  loading: boolean;
  error: string | null;
  refresh: (
    condoId: number | null,
    status?: string,
    assetId?: number | null,
  ) => Promise<void>;
  casesCount: number;
  selectedCaseId: number | null;
  setSelectedCaseId: (caseId: number | null) => void;
}

const CasesContext = createContext<CasesContextType | undefined>(undefined);

export function CasesProvider({ children }: { children: ReactNode }) {
  const [cases, setCases] = useState<CondoCase[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);

  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";

  const fetchCases = useCallback(
    async (condoId: number | null, status?: string, assetId?: number | null) => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (condoId) {
          params.set("condo", condoId.toString());
        }
        if (status) {
          params.set("status", status);
        }
        if (assetId) {
          params.set("asset", assetId.toString());
        }
        const queryString = params.toString();
        const url = queryString
          ? `/manager-api/cases/?${queryString}`
          : `/manager-api/cases/`;
        const casesData = await fetchWithCredentials(url);
        setCases(casesData);
      } catch (err: any) {
        console.error(err);
        setError(
          err.message ||
            (locale === "es"
              ? "Error al cargar los incidentes"
              : "Failed to load cases")
        );
      } finally {
        setLoading(false);
      }
    },
    [locale]
  );

  const refresh = useCallback(
    async (condoId: number | null, status?: string, assetId?: number | null) => {
      await fetchCases(condoId, status, assetId);
    },
    [fetchCases]
  );

  const casesCount = useMemo(() => cases.length, [cases]);

  return (
    <CasesContext.Provider
      value={{
        cases,
        loading,
        error,
        refresh,
        casesCount,
        selectedCaseId,
        setSelectedCaseId,
      }}
    >
      {children}
    </CasesContext.Provider>
  );
}

export function useCases() {
  const context = useContext(CasesContext);
  if (!context) {
    throw new Error("useCases must be used within a CasesProvider");
  }
  return context;
}
